import { FC, Fragment, useEffect, useState } from 'react'
import { PureQueryOptions } from '@apollo/client'
import isNumber from 'lodash/isNumber'
import Link from 'next/link'
import styled from 'styled-components'
import {
  useGqlUserScrapListQuery,
  useUserScrapDeleteAllMutation,
  UserScrapType,
  GqlUserScrapListDocument,
  GqlUserScrapCountsDocument,
  UserScrapOrderField,
  OrderDirection,
  CoverLetterDetailPage_CoverLetterFragment,
  CoverLetterDetailPage_CoverLetterFragmentDoc,
  useCurrentUserScrapsDeleteAllCommunityMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import { LIST_PAGE_SIZE } from 'constants/paginations'
import useFlutterInappwebview from 'hooks/useFlutterInappwebview'
import CoverLetterParagraphScrapListItem from 'screens/CoverLetterParagraphScrapScreen/CoverLetterParagraphScrapListItem'
import { getCoverLetterItemLinkProps } from 'screens/CoverLetterScreen/CoverLetterList/CoverLetterListContent'
import CoverLetterListItemMobile from 'screens/CoverLetterScreen/mobile/components/CoverLetterList/CoverLetterListItemMobile'
import Close from 'svgIcons/Close'
import ActivityListItem from '../../../../../components/ActivityListItem'
import Pagination from '../../../../../components/Pagination'
import { useScrapStateContext } from '../../../../../screens/ScrapScreen/useScrapState'
import CommunityScrapList from '../../component/CommunityScrapList'
import ScrapDeleteAllModal from '../../component/ScrapDeleteAllModal'
import ScrapListNotFound from '../../screen/ScrapListNotFound'
import CoverLetterScrapListFilterMobile from './CoverLetterScrapListFilter.mobile'

const LabelByUserScrapType = {
  [UserScrapType.ACTIVITY]: '공고',
  [UserScrapType.COVER_LETTER_AND_PARAGRAPH]: '자소서와 문장',
  [UserScrapType.COVER_LETTER]: '자소서',
  [UserScrapType.COVER_LETTER_PARAGRAPH]: '문장',
  [UserScrapType.COMMUNITY]: '커뮤니티',
  [UserScrapType.NEWS_ITEM]: '채널',
}

const ScrapListMobile: FC = () => {
  const isWebview = useIsWebview()
  const { fluterInappwebview } = useFlutterInappwebview()
  const context = useScrapStateContext()
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const { tabState, pagination, page } = context
  const pageSize = LIST_PAGE_SIZE

  const { data, loading, variables, error } = useGqlUserScrapListQuery({
    variables: {
      filterBy: {
        userScrapType: UserScrapType[tabState] || UserScrapType.ACTIVITY,
      },
      pagination: {
        page,
        pageSize,
      },
      orderBy: {
        field: UserScrapOrderField.CREATED_AT,
        direction: OrderDirection.DESC,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const [deleteAllUserScraps] = useUserScrapDeleteAllMutation()
  const [deleteAllUserScrapsCommunity] =
    useCurrentUserScrapsDeleteAllCommunityMutation()

  const queries: PureQueryOptions[] = [
    {
      query: GqlUserScrapListDocument,
      variables,
    },
    { query: GqlUserScrapCountsDocument },
  ]

  const isCoverLetter =
    UserScrapType[tabState] !== UserScrapType.ACTIVITY &&
    UserScrapType[tabState] !== UserScrapType.COMMUNITY

  const totalCount = data?.userScraps?.totalCount

  useEffect(() => {
    if (isNumber(totalCount)) {
      pagination.setNumberOfPages(Math.ceil(totalCount / pageSize))
      window.scrollTo(0, 0)
    }
  }, [pageSize, totalCount])

  async function handleDeleteAllUserScraps() {
    if (UserScrapType[tabState] === UserScrapType.COMMUNITY) {
      await deleteAllUserScrapsCommunity({
        refetchQueries: queries,
      })
    } else {
      await deleteAllUserScraps({
        variables: {
          userScrapType: UserScrapType[tabState] || UserScrapType.ACTIVITY,
        },
        refetchQueries: queries,
        update: (cache, { data }) => {
          if (
            tabState === UserScrapType.ACTIVITY ||
            tabState === UserScrapType.COVER_LETTER
          ) {
            return
          }

          const coverLetterParagraphScraps =
            data?.userScrapDeleteAll?.coverLetterParagraphScraps

          if (!coverLetterParagraphScraps) {
            return
          }

          const coverLetterIDs = coverLetterParagraphScraps.map(
            (scrap) => scrap.coverLetter?.id,
          )

          for (const coverLetterID of coverLetterIDs) {
            if (!coverLetterID) {
              continue
            }

            const cacheID = `CoverLetter:${coverLetterID}`
            const fragmentName = 'CoverLetterDetailPage_coverLetter'

            const cachedCoverLetterFragmentData =
              cache.readFragment<CoverLetterDetailPage_CoverLetterFragment>({
                id: cacheID,
                fragmentName,
                fragment: CoverLetterDetailPage_CoverLetterFragmentDoc,
              })

            if (!cachedCoverLetterFragmentData) {
              continue
            }

            cache.writeFragment({
              id: cacheID,
              fragmentName,
              fragment: CoverLetterDetailPage_CoverLetterFragmentDoc,
              data: {
                ...cachedCoverLetterFragmentData,
                viewerParagraphScraps: [],
              },
            })
          }
        },
      })
    }

    if (isWebview) {
      fluterInappwebview?.callHandler('reportRemoveAllScraps')
    }
    setShowDeleteAlert(false)
  }

  const handleShowDeleteAlert = () => {
    setShowDeleteAlert((prev) => !prev)
  }

  const renderList = () => {
    const nodes = data?.userScraps?.nodes || []

    if (nodes.length === 0 || !!error) {
      return <ScrapListNotFound />
    }

    if (nodes[0]?.__typename === 'UserScrapCommunity') {
      return (
        <CommunityScrapList
          dataNodes={nodes}
          isCoverLetter={isCoverLetter}
          isLoading={loading}
          queries={queries}
        />
      )
    }

    return (
      <>
        {nodes.map((node) => {
          if (!node || node.__typename !== 'UserScrap') {
            return
          }

          const userScrapType = node.scrapType

          switch (userScrapType) {
            case UserScrapType.ACTIVITY: {
              const activity = node.activity

              if (!activity) {
                return
              }

              return (
                <Fragment key={node.id}>
                  <ActivityListItem
                    size="large"
                    activity={activity}
                    showActivityType
                    currentUser={data?.currentUser}
                    scrapIcon={<Close className="close-icon" />}
                    showScrapCount={false}
                    scrapRefetchQueries={queries}
                  />
                  <hr className="divider" />
                </Fragment>
              )
            }
            case UserScrapType.COVER_LETTER: {
              const coverLetter = node.coverLetter

              if (!coverLetter) {
                return
              }

              const LinkProps = getCoverLetterItemLinkProps(
                context,
                coverLetter.id,
              )

              return (
                <div className="cover-letter-wrapper" key={node.id}>
                  <CoverLetterListItemMobile
                    coverLetter={coverLetter}
                    currentUser={data?.currentUser}
                    scrapIcon={<Close className="close-icon" />}
                    showScrapCount={false}
                    scrapRefetchQueries={queries}
                    LinkProps={LinkProps}
                  />
                  <hr className="divider" />
                </div>
              )
            }
            case UserScrapType.COVER_LETTER_PARAGRAPH: {
              const coverLetterParagraphScrap = node.coverLetterParagraphScrap

              if (!coverLetterParagraphScrap) {
                return
              }
              const LinkProps = getCoverLetterItemLinkProps(
                context,
                coverLetterParagraphScrap.coverLetter?.id,
              )

              return (
                <div className="cover-letter-wrapper" key={node.id}>
                  <CoverLetterParagraphScrapListItem
                    currentUser={data?.currentUser}
                    coverLetterParagraphScrap={coverLetterParagraphScrap}
                    scrapRefetchQueries={queries}
                    LinkProps={LinkProps}
                  />
                </div>
              )
            }
            default: {
              break
            }
          }
        })}
      </>
    )
  }

  return (
    <StyledWrapper>
      <div>
        <div className="button-box">
          <Link
            href={{
              pathname: getWebviewHref('/calendar', isWebview),
              query: { activityType: 'activity', tab: 'myActivity' },
            }}
          >
            <Button
              className="calendar-button"
              color="black"
              variant="outlined"
            >
              내 스크랩 달력
            </Button>
          </Link>
          <Button
            className="delete-button"
            color="lightGrey"
            disabled={data?.userScraps?.totalCount === 0}
            onClick={handleShowDeleteAlert}
          >
            모두삭제
          </Button>
        </div>
        <div className="divider" />
        {isCoverLetter && <CoverLetterScrapListFilterMobile />}
        {renderList()}
      </div>
      {isNumber(totalCount) && totalCount > 0 && (
        <div
          className={isWebview ? 'webview-pagination-box' : 'pagination-box'}
        >
          <Pagination pagination={pagination} />
        </div>
      )}
      <ScrapDeleteAllModal
        open={showDeleteAlert}
        onClose={handleShowDeleteAlert}
        onConfirm={handleDeleteAllUserScraps}
      >
        <DeleteDialogContent>
          잠깐, 스크랩한{' '}
          <span className="primary-text">
            {totalCount}건의 {LabelByUserScrapType[tabState]}
          </span>
          를<br /> 모두 삭제하실건가요?
        </DeleteDialogContent>
      </ScrapDeleteAllModal>
    </StyledWrapper>
  )
}

export default ScrapListMobile

const StyledWrapper = styled.div`
  margin-top: 0px;

  .close-icon {
    width: 16px;
    height: 16px;
    path {
      fill: #333;
    }
  }
  .button-box {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 0;
    padding: 0 20px;
    height: 0px;
    gap: 0px;
  }

  .cover-letter-wrapper {
    padding: 0px 20px;
    background-color: #fff;
  }

  .delete-button {
    font-size: 12px;
    letter-spacing: -0.2px;
    width: inherit;
    height: 36px;
    transform: translate(0, -72px);
    padding: 0 5px;
    font-weight: 400;
  }

  .divider {
    border: none;
    height: 1px;
    background-color: #eeeeee;
    margin: 0px;
  }

  .calendar-button {
    font-size: 12px;
    letter-spacing: -0.2px;
    width: inherit;
    height: 36px;
    transform: translate(0, -72px);
    padding: 8px 13px;
    margin-right: 16px;
  }

  .pagination-box {
    padding-bottom: 20px;
  }

  .webview-pagination-box {
    padding-bottom: 70px;
    background-color: #fafafa;
  }
`

const DeleteDialogContent = styled.div`
  text-align: center;
  .primary-text {
    font-size: inherit;
    font-weight: inherit;
    color: #01a0ff;
  }
`
